import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
    Button,
    Flex,
    Box, Stack, MenuButton, MenuList, MenuItem, Menu,
} from "@chakra-ui/react";
import {
    FaFileAlt,
    FaFileUpload,
    FaEdit,
    FaEye,
    FaTrashAlt,
    FaRegCheckCircle,
    FaRegTimesCircle,
    FaCopy,
    FaPrint,
} from "react-icons/fa";
import { AddIcon } from "@chakra-ui/icons";
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import ConfirmDialog from "components/Dialog/ConfirmDialog";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import Loading from "../../../components/Loading/Loading";
import SearchTable2 from "components/Tables/SearchTable2";
import { permitteesService } from "services/permitteesService";
import { getEmptyPageResponse, toTablePageableData } from "utils";
import { useCustomToast } from "../../../components/CustomToast/CustomToast";
import { ROUTES } from "../../../constants/routes-path";
import { PermitteesFilter } from "./PermitteesFilter";
import { PERMITTEES_LIST_URL_DEFAULTS } from "utils/listUrlSync";
import { useListUrlSync } from "hooks/useListUrlSync";
import { ListFiltersActiveAlert } from "components/ListFiltersActiveAlert/ListFiltersActiveAlert";
import { SITUATION } from "../../../constants/situation";
import { IoIosArrowDown } from "react-icons/io";

function PermitteesList() {
    const { t } = useTranslation();
    const { searchParams, setSearchParams, hasActiveFilters } = useListUrlSync(PERMITTEES_LIST_URL_DEFAULTS);
    const [response, setResponse] = useState(undefined);
    const dialogRef = useRef(undefined);
    const toggleStatusDialogRef = useRef(undefined);
    const showToast = useCustomToast();
    const navigate = useNavigate();
    const [permitteeToDelete, setPermitteeToDelete] = useState(null);
    const [permitteeToToggleStatus, setPermitteeToToggleStatus] = useState(null);
    const [isLoadingResponse, setIsLoadingResponse] = useState(false);

    const columns = useMemo(() => [
        {
            Header: t("permittees.list.table.header.vehicle-plate"),
            accessor: "placa",
            id: "placaVeiculo"
        },
        {
            Header: t("permittees.list.table.header.permit-type"),
            accessor: "tipo",
            id: "idPontoPermissionario.tipo"
        },
        {
            Header: t("permittees.list.table.header.permit-square"),
            accessor: "praca",
            id: "idPontoPermissionario.logradouro"
        },
        {
            Header: t("permittees.list.table.header.spot"),
            accessor: "ponto",
            id: "idPontoPermissionario.numero"
        },
        {
            Header: t("permittees.list.table.header.cpfcnpj"),
            accessor: "cpf_cnpj",
            id: "cpfCnpjProprietario"
        },
        {
            Header: t("permittees.list.table.header.name"),
            accessor: "nome",
            id: "nomeProprietario"
        },
        {
            Header: t("permittees.list.table.header.photo"),
            disableSortBy: true,
            accessor: "foto",
        },
        {
            Header: t("permittees.list.table.header.situation"),
            accessor: "situacao",
            id: "status"
        },
        {
            Header: t("permittees.list.table.header.date"),
            accessor: "data",
            id: "dataCriacao"
        },
        {
            Header: t("permittees.list.table.header.actions"),
            disableSortBy: true,
            actions: [
                {
                    label: t("system.actions.details"),
                    icon: <FaEye />,
                    onClick: row => navigate(
                        ROUTES.permitteeDetailsPath.replace(":id", row.original.id)
                    )
                },
                {
                    label: t('system.actions.edit'),
                    onClick: (row) => navigate(ROUTES.permitteesEditPath.replace(":id", row.original.id)),
                    icon: <FaEdit />
                },
                {
                    label: t('system.actions.copy'),
                    onClick: (row) => navigate(`${ROUTES.permitteesAddPath}?copy_id=${row.original.id}`),
                    icon: <FaCopy />
                },
                {
                    label: t('system.actions.deactivate'),
                    onClick: (row) => { handleDeactivate(row.original.id) },
                    visible: values => values.situacao === SITUATION.ATIVO,
                    icon: <FaRegTimesCircle />,
                },
                {
                    label: t('system.actions.activate'),
                    onClick: (row) => {
                        handleActivate(row.original.id)
                    },
                    visible: values => values.situacao === SITUATION.INATIVO,
                    icon: <FaRegCheckCircle />,
                },
                {
                    label: t('permittees.list.actions.documents'),
                    onClick: (row) =>
                        navigate(`${ROUTES.permitteeDocumentsPath}/?parent_id=${row.original.id}`),
                    icon: <FaFileAlt />
                },
                {
                    label: t('permittees.list.actions.additional-documents'),
                    onClick: (row) =>
                        navigate(`${ROUTES.documentsPath}/?parent_id=${row.original.id}&parent_type=permissionarios`),
                    icon: <FaFileUpload />
                },
                {
                    label: t('system.actions.remove'),
                    onClick: (row) => { handleRemove(row.original.id) },
                    icon: <FaTrashAlt color="#E53E3E" />,
                    visible: values => values.situacao === SITUATION.INATIVO,
                }
            ],

        }
    ], []);

    const fetchData = async (params = {}) => {
        params.orderBy = params.orderBy ?? searchParams.orderBy ?? "dataCriacao";
        params.orderDirection = params.orderDirection ?? searchParams.orderDirection ?? "desc";
        if (response) setResponse(undefined);

        try {
            params = { ...searchParams, ...params };
            setSearchParams(params);
            const data = await permitteesService.list(params);
            setResponse(data);
        } catch (err) {
            setResponse(getEmptyPageResponse(searchParams));
            showToast({ title: t(err), status: "error" });
        }
    };

    const clearPermitteesFilters = () => {
        fetchData({
            page: 1,
            filters: {
                placa: null,
                id_ponto_permissionario: null,
                numero: null,
                cpf_cnpj_proprietario: null,
                data_inicial: null,
                data_final: null,
                tipo: null,
                nome_proprietario: null,
                status_vistoria: null,
            },
        });
    };

    const handleRemove = (permitteeId) => {
        setPermitteeToDelete(permitteeId);
        dialogRef.current?.displayDialog({
            title: t("permittees.list.actions.remove-permittee"),
            message: t("system.actions.remove-confirm"),
        });
    };

    const handleConfirmRemove = async () => {
        if (permitteeToDelete) {
            try {
                setIsLoadingResponse(true);
                await permitteesService.delete(permitteeToDelete);
                showToast({
                    title: t("permittees.messages.success.delete"),
                    status: "success",
                });
                await fetchData();
            } catch (err) {
                showToast({ title: t(err), status: "error" });
            } finally {
                setIsLoadingResponse(false);
            }
        }
    };

    const handleDeactivate = (permitteeId) => {
        setPermitteeToToggleStatus(permitteeId);
        toggleStatusDialogRef.current?.displayDialog({
            title: t("permittees.list.actions.deactivate-permittee"),
            message: t("system.actions.deactivate-confirm"),
        });
    };

    const handleActivate = (permitteeId) => {
        setPermitteeToToggleStatus(permitteeId);
        toggleStatusDialogRef.current?.displayDialog({
            title: t("permittees.list.actions.activate-permittee"),
            message: t("system.actions.activate-confirm"),
        });
    };

    const handleConfirmToggleStatus = async () => {
        if (permitteeToToggleStatus) {
            try {
                setIsLoadingResponse(true);
                await permitteesService.updateStatus(permitteeToToggleStatus);
                showToast({
                    title: t("permittees.messages.success.toggle-status"),
                    status: "success",
                });
                await fetchData();
            } catch (err) {
                showToast({ title: t(err), status: "error" });
            } finally {
                setIsLoadingResponse(false);
            }
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const printDocumentBatch = () => {
        navigate(ROUTES.transportManagementPath + ROUTES.permittee + ROUTES.permitteePrintDocumentBatch);
    }

    const printLicenseBatch = () => {
        navigate(ROUTES.transportManagementPath + ROUTES.permittee + ROUTES.permitteePrintLicenseBatch);
    }

    return (
        <Flex gap="5" direction="column" pt={{ base: "180px", sm: "155px", lg: "90px" }}>
            <Flex w="100%" px="30px" justify="space-between" align="center" direction={{ sm: "column", md: "row" }}>
                <Button
                    variant="list-primary"
                    leftIcon={<AddIcon fontSize="sm" />}
                    alignSelf={{ sm: "flex-start", lg: null }}
                    onClick={() => navigate(ROUTES.permitteesAddPath)}
                >
                    {t("permittees.new.button")}
                </Button>

                <Stack
                    direction="row"
                    pt={{ sm: "20px", md: "0",  lg: "0" }}
                    spacing="10px"
                    alignSelf={{ sm: "flex-start", lg: "auto" }}
                >
                    {/*Filters menu*/}
                    <PermitteesFilter
                        filterSnapshot={searchParams.filters}
                        onFilter={(filters) => { fetchData({ filters })} }
                    />

                    <Menu>
                        <MenuButton
                            w="125px"
                            variant="list-primary"
                            rightIcon={<IoIosArrowDown />}
                            as={Button}
                        >
                            {t("system.labels.actions")}
                        </MenuButton>

                        <MenuList variant="light">
                            <MenuItem onClick={printDocumentBatch} icon={<FaPrint />}>
                                {t("permittees.list.actions.print-document-batch")}
                            </MenuItem>

                            <MenuItem onClick={printLicenseBatch} icon={<FaPrint />}>
                                {t("permittees.list.actions.print-license-batch")}
                            </MenuItem>
                        </MenuList>
                    </Menu>
                </Stack>
            </Flex>
            <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
                <ListPageCardHeader title={t("permittees.title")} subtitle={t("permittees.description")} />
                <CardBody>
                    <ListFiltersActiveAlert show={hasActiveFilters} onClear={clearPermitteesFilters} />
                    {response ? (
                        <Box position="relative">
                            <SearchTable2
                                columnsData={columns}
                                tableData={response.content}
                                pageableData={toTablePageableData(response, { defaultField: "date", ...searchParams })}
                                onChange={fetchData}
                                disableSearch={true}
                            />
                            {(isLoadingResponse) && (
                                <Box
                                    position="fixed"
                                    top="0"
                                    left="0"
                                    right="0"
                                    bottom="0"
                                    bg="rgba(255, 255, 255, 0.6)"
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    zIndex="overlay"
                                >
                                    <Loading maxH={{ sm: "100px", md: "350px" }} />
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Loading maxH={{ sm: "100px", md: "350px" }} />
                    )}
                </CardBody>
            </Card>

            <ConfirmDialog ref={dialogRef} onConfirm={handleConfirmRemove} confirmButtonProps={{ colorScheme: "red" }} />
            <ConfirmDialog ref={toggleStatusDialogRef} onConfirm={handleConfirmToggleStatus} confirmButtonProps={{ colorScheme: "red" }} />
        </Flex>
    );
}

export default PermitteesList;
